import {
  CloseButton,
  Flex,
  Select,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUpdateCUAssigned } from "../../../api/hooks/creditLimit/useUpdateCUAssigned.ts";
import { useLimitRequests } from "../../../api/hooks/limitRequest/useLimitRequests.ts";
import { useUpdateLimitRequestInline } from "../../../api/hooks/limitRequest/useUpdateLimitRequestInline.ts";
import SectionTitle from "../../../components/Section/SectionTitle/SectionTitle.tsx";
import Table from "../../../components/Table/Table.tsx";
import { SortDirection, TableRow } from "../../../components/Table/types.ts";
import { AppRoute } from "../../../constants.ts";
import {
  LimitRequestFilter,
  LimitRequestOrderBy,
  LimitRequestStatusFilterOptions,
  OrderBy,
  StringFilterMode,
  UpdateCuAssignedInput,
  UpdateLimitRequestInlineInput,
} from "../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { CustomerOption } from "../../../types/customer.ts";
import {
  LimitRequest,
  limitRequestTypeOptions,
} from "../../../types/limitRequest/limitRequest.ts";
import { UserOption } from "../../../types/user.ts";
import {
  getCompanyOptions,
  getSalesNameOptions,
  getSalesTeamOptions,
} from "../../../utils/company.ts";
import { getLimitRequestApiFilter } from "../../../utils/filter.ts";
import { getPagesCount } from "../../../utils/pagination.ts";
import { getLimitRequestApiSort } from "../utils.ts";
import { useNewLimitRequestedTableData } from "./hooks/useNewLimitRequestedTableData.tsx";

interface Props {
  companies: CustomerOption[] | undefined;
  assignees: UserOption[] | undefined;
}

const NewLimitRequestedTable = ({ companies, assignees }: Props) => {
  const navigate = useNavigate();

  const [filter, setFilter] = useState<LimitRequestFilter>({});
  const [search, setSearch] = useState<string | undefined>();
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [orderBy, setOrderBy] = useState<LimitRequestOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [page, setPage] = useState<number>(1);

  const [{ data, fetching, error }, refreshData] = useLimitRequests({
    filter: {
      ...filter,
      cuAssignedId: null,
      requestStatus: {
        equals: LimitRequestStatusFilterOptions.NewLimitRequest,
      },
      limitName: debouncedSearch
        ? {
            contains: debouncedSearch,
            mode: StringFilterMode.Insensitive,
          }
        : undefined,
    },
    orderBy,
    page,
  });

  const [, updateCUAssigned] = useMutationNotificationWrapper(
    useUpdateCUAssigned(),
    {
      success: { message: "CU Assigned has been successfully updated." },
    }
  );

  const [, updateLimitRequestCategory] = useMutationNotificationWrapper(
    useUpdateLimitRequestInline(),
    {
      success: { message: "Limit Request has been successfully updated." },
    }
  );

  const newLimitRequests: LimitRequest[] | undefined | null =
    data?.limitRequests?.data;

  const pageCount = getPagesCount(data?.limitRequests?.total);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getLimitRequestApiSort(sort));
    },
    [setOrderBy]
  );

  const handleEditCUAssigned = useCallback(
    (id: number, input: UpdateCuAssignedInput) => {
      updateCUAssigned({ id, input }).then(refreshData);
    },
    [updateCUAssigned, refreshData]
  );

  const handleEdit = useCallback(
    (id: number, input: UpdateLimitRequestInlineInput) => {
      updateLimitRequestCategory({ id, input }).then(refreshData);
    },
    [refreshData, updateLimitRequestCategory]
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilter(getLimitRequestApiFilter({ [key]: value }));
  }, []);

  const handleRowClik = useCallback(
    (record: TableRow) => {
      navigate(`${AppRoute.LimitRequest}/${record.id}`);
    },
    [navigate]
  );

  const [columns, rows] = useNewLimitRequestedTableData({
    data: newLimitRequests,
    assignees,
    onEdit: handleEdit,
    onEditCUAssigned: handleEditCUAssigned,
  });

  if (error) {
    notifications.clean();
    notifications.show({
      message:
        "Something went wrong while trying to fetch Limit Requests data.",
      variant: "error",
    });
  }

  return (
    <Flex direction="column" gap={12}>
      <SectionTitle>New Limit Requested</SectionTitle>
      <SimpleGrid
        cols={3}
        breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        spacing={20}
        verticalSpacing={12}
        pb={8}
      >
        <Select
          size="m"
          clearable
          searchable
          label="Sales Name"
          placeholder="Select Sales Name"
          data={getSalesNameOptions(companies)}
          onChange={(value) => handleFilterChange("salesName", value)}
        />
        <Select
          size="m"
          clearable
          searchable
          label="Sales Team"
          placeholder="Select Sales Team"
          data={getSalesTeamOptions(companies)}
          onChange={(value) => handleFilterChange("salesTeamName", value)}
        />
        <Select
          size="m"
          clearable
          searchable
          label="Company name"
          placeholder="Select Company name"
          data={getCompanyOptions(companies)}
          onChange={(value) => handleFilterChange("companyName", value)}
        />
        <Select
          size="m"
          clearable
          label="Limit Type"
          placeholder="Select Limit Type"
          data={limitRequestTypeOptions}
          onChange={(value) => handleFilterChange("limitRequestType", value)}
        />
        <TextInput
          size="m"
          label="Limit Name"
          placeholder="Enter Limit Name"
          rightSection={
            <CloseButton
              aria-label="Clear input"
              variant="transparent"
              size="md"
              mr="5px"
              style={{
                display: search ? undefined : "none",
              }}
              onClick={() => setSearch("")}
            />
          }
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </SimpleGrid>
      <Table
        columns={columns}
        rows={rows}
        defaultSort={{
          columnAccessor: "createdAt",
          direction: SortDirection.asc,
        }}
        loading={fetching}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        onRowClick={handleRowClik}
        onSortChange={handleSortChange}
      />
    </Flex>
  );
};

export default NewLimitRequestedTable;
